import { Link } from 'react-router-dom';
import type { Tour } from '../../../mocks/tours';
import { useCart } from '../../../hooks/useCart';
import { useState } from 'react';

interface Props {
  tour: Tour;
}

const categoryLabels: Record<Tour['category'], string> = {
  gunubirlik: 'Günübirlik Tur',
  konaklamali: 'Konaklamalı Tur',
  ulasimli: 'Ulaşımlı Tatil',
  yurtdisi: 'Yurt Dışı Tur',
};

const categoryPaths: Record<Tour['category'], string> = {
  gunubirlik: '/gunubirlik-turlar',
  konaklamali: '/konaklamali-turlar',
  ulasimli: '/ulasimli-tatil',
  yurtdisi: '/yurtdisi-turlar',
};

export default function TourDetailHero({ tour }: Props) {
  const { addToCart, isInCart } = useCart();
  const [added, setAdded] = useState(false);
  const inCart = isInCart(tour.id);

  const handleAdd = () => {
    addToCart(tour);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <section className="relative h-96 mt-0 overflow-hidden">
      <img
        src={tour.image}
        alt={tour.name}
        className="w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

      {/* Breadcrumb */}
      <div className="absolute top-24 left-0 right-0 px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-white/80 text-sm">
            <Link to="/" className="hover:text-orange-300 transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={categoryPaths[tour.category]} className="hover:text-orange-300 transition-colors cursor-pointer">
              {categoryLabels[tour.category]}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/60 line-clamp-1">{tour.name}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
              {categoryLabels[tour.category]}
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl mb-3">
              {tour.name}
            </h1>
            <div className="flex items-center gap-5 flex-wrap">
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line" />
                </div>
                <span>{tour.duration} {tour.durationUnit}</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line" />
                </div>
                <span>Ankara Çıkışlı</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-group-line" />
                </div>
                <span>Grup Turu</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <p className="text-white/70 text-xs mb-0.5">kişi başı fiyat</p>
              <p className="text-3xl font-bold text-orange-400">
                {tour.currency === 'TL' ? `₺${tour.price.toLocaleString('tr-TR')}` : `€${tour.price.toLocaleString('tr-TR')}`}
              </p>
            </div>
            <button
              onClick={handleAdd}
              disabled={inCart || added}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${inCart || added ? 'bg-green-500 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${inCart || added ? 'ri-check-line' : 'ri-shopping-cart-add-line'} text-base`} />
              </div>
              <span>{inCart || added ? 'Sepete Eklendi' : 'Sepete Ekle'}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
